import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from "@angular/core";
import { Router } from "@angular/router";
import { MainState } from "@app/store/state";
import {
  ImageInterface,
  ImageRevisionInterface,
  ORIGINAL_REVISION_LABEL,
  DownloadLimitationOptions
} from "@core/interfaces/image.interface";
import { ClassicRoutesService } from "@core/services/classic-routes.service";
import { CollapseSyncService } from "@core/services/collapse-sync.service";
import { DeviceService } from "@core/services/device.service";
import { ImageService } from "@core/services/image/image.service";
import { ImageViewerService } from "@core/services/image-viewer.service";
import { MobilePageMenuService } from "@core/services/mobile-page-menu.service";
import { SearchService } from "@core/services/search.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { ImageViewerSectionBaseComponent } from "@shared/components/misc/image-viewer/image-viewer-section-base.component";
import { CookieService } from "ngx-cookie";

@Component({
  selector: "astrobin-image-viewer-title",
  template: `
    <ng-container *ngIf="currentUserWrapper$ | async as currentUserWrapper">
      <div class="image-viewer-title d-flex flex-row justify-content-between align-items-center gap-2 px-2">
        <h2 class="flex-grow-1 mb-0 text-center text-sm-start">
          <span [innerHTML]="image.title | highlight: searchModel?.text?.value"></span>

          <small
            *ngIf="
              revision && revisionLabel !== ORIGINAL_REVISION_LABEL && revision.title && revision.title !== image.title
            "
            class="revision-title justify-content-center justify-content-sm-start d-block"
          >
            <span [innerHTML]="revision.title | highlight: searchModel?.text?.value"></span>
          </small>

          <small
            *ngIf="currentUserWrapper.user?.id === image.user && image.uploaderName"
            class="justify-content-center justify-content-sm-start"
          >
            <span [innerHTML]="image.uploaderName" class="original-filename"></span>
          </small>

          <small class="justify-content-center justify-content-sm-start flex-wrap">
            <span *ngIf="publicationDate">
              <fa-icon
                *ngIf="licenseIcon && licenseTooltip"
                [icon]="licenseIcon"
                [ngbTooltip]="licenseTooltip"
                class="license-icon"
                container="body"
                triggers="hover click"
              ></fa-icon>
              {{ publicationDate | localDate | timeago }}
            </span>

            <span class="view-count">
              <span *ngIf="image.viewCount === 0" [translate]="'No views'"></span>
              <span *ngIf="image.viewCount === 1" [translate]="'One view'"></span>
              <span
                *ngIf="image.viewCount > 1"
                [translate]="'{{0}} views'"
                [translateParams]="{ '0': image.viewCount | numberSuffix }"
              ></span>
            </span>

            <span *ngIf="resolution" [innerHTML]="resolution" class="resolution"></span>

            <span *ngIf="size" [innerHTML]="size | filesize" class="file-size"></span>

            <a *ngIf="image.link" [href]="image.link" class="no-external-link-icon" rel="noopener" target="_blank">
              <fa-icon class="me-1" icon="external-link-alt"></fa-icon>
              {{ "Link" | translate }}
            </a>

            <a
              *ngIf="image.linkToFits"
              [href]="image.linkToFits"
              class="no-external-link-icon"
              rel="noopener"
              target="_blank"
            >
              <fa-icon class="me-1" icon="file"></fa-icon>
              {{ "TIFF/FITS" | translate }}
            </a>
          </small>

          <div class="iotd-tp">
            <div *ngIf="!image.iotdDate && (image.isTopPick || image.isTopPickNomination)">
              <span *ngIf="!image.iotdDate && image.isTopPick" class="top-pick">
                <span class="label">
                  <fa-icon icon="star"></fa-icon>
                  {{ "Top Pick" | translate }}
                </span>
                <ng-container [ngTemplateOutlet]="iotdInfoLinkTemplate"></ng-container>
              </span>

              <span
                *ngIf="!image.iotdDate && !image.isTopPick && image.isTopPickNomination"
                class="top-pick-nomination"
              >
                <span class="label">
                  <fa-icon icon="arrow-up"></fa-icon>
                  {{ "Top Pick Nomination" | translate }}
                </span>
                <ng-container [ngTemplateOutlet]="iotdInfoLinkTemplate"></ng-container>
              </span>
            </div>

            <span
              *ngIf="
                currentUserWrapper.user?.id === image.user &&
                !image.iotdDate &&
                !image.isTopPick &&
                !image.isTopPickNomination &&
                image.isInIotdQueue
              "
              class="in-iotd-queue"
            >
              <a (click)="viewIotdTpStats()" astrobinEventPreventDefault astrobinEventStopPropagation href="#">
                <span class="label">
                  <fa-icon icon="gavel"></fa-icon>
                  {{ "Currently in the IOTD/TP queues" | translate }}
                </span>

                <fa-icon class="ms-2 d-inline-block" icon="info-circle"></fa-icon>
              </a>
            </span>
          </div>
        </h2>

        <astrobin-image-viewer-share-button
          [image]="image"
          [revisionLabel]="revisionLabel"
          class="d-none d-lg-block p-1"
        ></astrobin-image-viewer-share-button>

        <button
          *ngIf="shouldShowKebabMenu(currentUserWrapper)"
          [attr.data-menu-id]="getCurrentMenuId()"
          (click)="openMenu()"
          class="kebab-menu btn btn-link link-secondary d-none d-lg-block p-1"
        >
          <fa-icon class="m-0" icon="ellipsis-v"></fa-icon>
        </button>
      </div>
    </ng-container>

    <ng-template #iotdInfoLinkTemplate>
      <a class="ms-2 no-external-link-icon" href="https://welcome.astrobin.com/iotd" rel="noopener" target="_blank">
        <fa-icon icon="info-circle"></fa-icon>
      </a>
    </ng-template>

    <ng-template #viewIotdTpStatsOffcanvas let-offcanvas>
      <div class="offcanvas-header">
        <h5 class="offcanvas-title">
          {{ "IOTD/TP stats" | translate }}
          <fa-icon class="ms-2" icon="lock"></fa-icon>
        </h5>
        <button (click)="offcanvas.dismiss()" class="btn-close" type="button"></button>
      </div>
      <div class="offcanvas-body">
        <astrobin-image-viewer-iotd-tp-stats [image]="image"></astrobin-image-viewer-iotd-tp-stats>
      </div>
    </ng-template>
  `,
  styleUrls: ["./image-viewer-title.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageViewerTitleComponent extends ImageViewerSectionBaseComponent implements OnChanges {
  @ViewChild("viewIotdTpStatsOffcanvas") viewIotdTpStatsOffcanvas: TemplateRef<any>;

  protected resolution: string;
  protected size: number;
  protected publicationDate: string;
  protected licenseIcon: IconProp;
  protected licenseTooltip: string;
  protected revision: ImageInterface | ImageRevisionInterface;
  protected readonly ORIGINAL_REVISION_LABEL = ORIGINAL_REVISION_LABEL;

  public constructor(
    public readonly store$: Store<MainState>,
    public readonly searchService: SearchService,
    public readonly router: Router,
    public readonly imageViewerService: ImageViewerService,
    public readonly imageService: ImageService,
    public readonly classicRoutesService: ClassicRoutesService,
    public readonly windowRefService: WindowRefService,
    public readonly offcanvasService: NgbOffcanvas,
    public readonly deviceService: DeviceService,
    public readonly cookieService: CookieService,
    public readonly collapseSyncService: CollapseSyncService,
    public readonly changeDetectorRef: ChangeDetectorRef,
    private readonly mobilePageMenuService: MobilePageMenuService
  ) {
    super(
      store$,
      searchService,
      router,
      imageViewerService,
      windowRefService,
      cookieService,
      collapseSyncService,
      changeDetectorRef
    );
  }

  /**
   * Open the image menu using the offcanvas
   */
  openMenu(): void {
    this.mobilePageMenuService.openMenu();
  }

  /**
   * Get the current menu ID for debugging purposes
   */
  getCurrentMenuId(): string | null {
    return this.mobilePageMenuService.getCurrentMenuId();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.image && changes.image.currentValue) ||
      (changes.revisionLabel && changes.revisionLabel.currentValue)
    ) {
      this.revision = this.imageService.getRevision(this.image, this.revisionLabel);
      this.setResolutionAndFileSize(this.image, this.revisionLabel);
      this.setPublicationDate(this.image);
      this.setLicenseIconAndTooltip(this.image);
    }
  }

  setResolutionAndFileSize(image: ImageInterface, revisionLabel: ImageRevisionInterface["label"]): void {
    this.resolution = `${this.revision.w}&times;${this.revision.h}`;
    this.size = this.revision.uploaderUploadLength;
  }

  setPublicationDate(image: ImageInterface): void {
    this.publicationDate = this.imageService.getPublicationDate(image);
  }

  setLicenseIconAndTooltip(image: ImageInterface): void {
    this.licenseIcon = this.imageService.getLicenseIcon(image.license);
    this.licenseTooltip = this.imageService.humanizeLicenseOption(image.license);
  }

  shouldShowKebabMenu(currentUserWrapper: any): boolean {
    if (!currentUserWrapper) {
      return false;
    }

    const isOwner = currentUserWrapper.user?.id === this.image.user;
    const hasDownloadAccess = this.image.downloadLimitation === DownloadLimitationOptions.EVERYBODY;
    const showClassicView = this.shouldShowClassicView(currentUserWrapper);

    // If user is owner, always show menu (they have owner-specific items)
    if (isOwner) {
      return true;
    }

    // If not owner, show menu only if there's download access OR classic view link
    return hasDownloadAccess || showClassicView;
  }

  shouldShowClassicView(currentUserWrapper: any): boolean {
    // Don't show in PWA mode
    if (this.deviceService.isPwa()) {
      return false;
    }

    // Don't show for anonymous users
    if (!currentUserWrapper?.user) {
      return false;
    }

    // Don't show for users who joined after 2025-01-01
    if (currentUserWrapper.user.dateJoined) {
      const joinDate = new Date(currentUserWrapper.user.dateJoined);
      const cutoffDate = new Date("2025-01-01");
      if (joinDate > cutoffDate) {
        return false;
      }
    }

    return true;
  }

  viewIotdTpStats() {
    this.offcanvasService.dismiss(); // Avoids nested offcanvases.
    this.offcanvasService.open(this.viewIotdTpStatsOffcanvas, {
      panelClass: "image-viewer-offcanvas image-iotd-tp-stats-offcanvas",
      backdropClass: "image-viewer-offcanvas-backdrop",
      position: this.deviceService.offcanvasPosition()
    });
  }
}
